import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useMemo } from "react";
import { getScreenLanguageKey } from "@/Components/Screen/ScreenLanguageProvider";

export type PickupScreenLayout = "vertical" | "horizontal";

export type PickupScreenSettings = {
  font_size: number;
  layout: PickupScreenLayout;
  language: string;
  show_customer_name: boolean;
  font_family: string;
  show_order_age: boolean;
  show_preparing_section: boolean;
  show_ready_section: boolean;
  sort_by: "receipt_number" | "oldest_first" | "newest_first";
  sound_on_ready: boolean;
  confirm_on_handover: boolean;
  card_size: "small" | "medium" | "large";
  max_orders_per_section: number;
};

export const defaultPickupScreenSettings: PickupScreenSettings = {
  font_size: 48,
  layout: "vertical",
  language: "nl",
  show_customer_name: false,
  font_family: "Arial",
  show_order_age: false,
  show_preparing_section: true,
  show_ready_section: true,
  sort_by: "receipt_number",
  sound_on_ready: false,
  confirm_on_handover: false,
  card_size: "large",
  max_orders_per_section: 24,
};

const STORAGE_KEY = "pickupScreenSettings";

/**
 * Local settings for the pickup screen.
 * Persisted in localStorage for now — swap atom for API query/mutation later.
 */
export const pickupScreenSettingsAtom = atomWithStorage<PickupScreenSettings>(
  STORAGE_KEY,
  defaultPickupScreenSettings
);

const usePickupScreenSettings = () => {
  const [stored, setSettings] = useAtom(pickupScreenSettingsAtom);

  const settings = useMemo(() => {
    const merged = { ...defaultPickupScreenSettings, ...stored };
    const rawLanguage = (stored as { language?: string | number } | null)
      ?.language;
    // Migrate old numeric country-language ids to i18n codes
    if (typeof rawLanguage === "number") {
      merged.language = getScreenLanguageKey(rawLanguage);
    }
    return merged;
  }, [stored]);

  return {
    settings,
    setSettings,
    isLoading: false,
  };
};

export default usePickupScreenSettings;
